import { Calendar } from '@berlitz/icons'
import PageTitle from '@components/PageTitle'
import PageWithRightSidebarTpl from '@components/PageWithRIghtSidebarTpl'
import WithPortalAuthorization from '@hocs/WithPortalAuthorization'
import WithUserTimeHOC, { WithUserTimeProps } from '@hocs/WithUserTime'
import WithLessonUtilsHOC, { WithLessonUtilsProp } from '@hocs/WithLessonUtils'
import { GroupClassListItem, InstructorGroupInfo } from '@interfaces/MySchedule'
import { ProfileProps } from '@interfaces/UserProfile'
import Layout from '@layouts/MySchedule/Layout'
import { LessonDate } from '@layouts/MySchedule/Layout/interface'
import QueryPageData, { GET_PAGE_DATA, getDateLessonCount } from '@layouts/MySchedule/QueryPageData'
import Sidebar from '@layouts/MySchedule/Sidebar'
import SchedulingLayout from '@layouts/Scheduling/SchedulingLayout'
import { setDate } from '@redux/actions/mySchedules'
import get from 'lodash/get'
import merge from 'lodash/merge'
import moment from 'moment'
import React from 'react'
import { withApollo, WithApolloClient } from 'react-apollo'
import { connect } from 'react-redux'
import { bindActionCreators, compose, Dispatch } from 'redux'
import { VIRTUAL_CLASSROOM_PROVIDER } from '@utils/constants'
import { utcfyDate } from '@layouts/Events/helpers'
import { EventAttendeeProvider } from '@layouts/Events/context'

interface MyScheduleProp {
  profile?: ProfileProps
  setDate: (val: string) => void
  date: string
}

interface MyScheduleState {
  fetched: boolean
  fetchingLessonDates: boolean
  currentDate: moment.Moment
  GroupClassList: GroupClassListItem[]
  lessonDates: LessonDate[]
  selectedDate: moment.Moment
}

type Props = WithApolloClient<MyScheduleProp & WithUserTimeProps & WithLessonUtilsProp>

export class MySchedule extends React.Component<Props, MyScheduleState> {
  constructor(props) {
    super(props)

    this.state = {
      fetched: false,
      fetchingLessonDates: false,
      currentDate: this.initCurrentDate(props),
      GroupClassList: [],
      lessonDates: [],
      selectedDate: this.initCurrentDate(props),
    }
  }

  initCurrentDate = (props) => {
    const date = moment(props.date, 'YYYY-MM-DD')
    if (!date.isValid()) {
      return props.getUserDate()
    }

    return this.props
      .getUserDate()
      .clone()
      .set('year', parseInt(date.format('YYYY'), 10))
      .set('month', parseInt(date.format('M'), 10) - 1)
      .set('date', parseInt(date.format('D'), 10))
  }

  getGroupClassList = (LessonGroups: InstructorGroupInfo[], currentDate: moment.Moment) =>
    get(
      // LessonGroups.find(({ InstructorGroupInfo: {GroupClassDate } }) =>
      LessonGroups.find(() =>
        // moment(GroupClassDate, 'YYYY-MM-DD').isSame(currentDate, 'day')
        moment(currentDate).isSame(currentDate, 'day')
      ),
      'InstructorGroupInfo.GroupClassList',
      []
    )

  setCurrentDate = (LessonGroups: InstructorGroupInfo[], date: Date) => {
    const selectedDate = moment(date)
    const currentDate = this.props
      .getUserDate()
      .set('year', parseInt(selectedDate.format('YYYY'), 10))
      .set('month', parseInt(selectedDate.format('M'), 10) - 1)
      .set('date', parseInt(selectedDate.format('D'), 10))

    this.setState({
      currentDate,
      GroupClassList: this.getGroupClassList(LessonGroups, currentDate),
    })

    this.props.setDate(currentDate.format())
  }

  handleMonthChange = async (date: Date) => {
    const { currentDate, lessonDates } = this.state
    const { client, profile, toUserDate } = this.props
    if (!currentDate.isSame(moment(date))) {
      this.setState({
        fetchingLessonDates: true,
        selectedDate: moment(date),
      })

      try {
        const params = {
          InstructorId: profile.SFId,
          StartDateTime: moment.utc(moment(date).startOf('month').startOf('week')).format(),
          EndDateTime: moment.utc(moment(date).endOf('month').endOf('week')).format(),
          FILTER_TYPE: 'Groups',
        }

        const res = await client.query({
          query: GET_PAGE_DATA,
          variables: { params },
          fetchPolicy: 'network-only',
        })

        this.setState({
          fetchingLessonDates: false,
          lessonDates: [
            ...lessonDates,
            ...getDateLessonCount(
              this.props.onlyValidLessons(get(res.data, 'getLessonGroups[0].InstructorGroupInfo.GroupClassList', [])),
              toUserDate
            ),
          ],
        })
      } catch (error) {
        this.setState({
          fetchingLessonDates: false,
        })
      }
    }
  }

  render() {
    const { fetched, currentDate, fetchingLessonDates, selectedDate } = this.state
    const { profile } = this.props
    const id = profile.SFId
    const startOfDay = currentDate.clone().startOf('day').add(1, 's')
    const startDate = moment
      .utc(startOfDay.clone())

      .format()
    const endDate = moment.utc(currentDate.clone().endOf('day')).format()

    return (
      // @ts-ignore
      <QueryPageData InstructorId={id} StartDateTime={startDate} EndDateTime={endDate} withLessonDates>
        {({ loading, data, loadingLessonDates, lessonDates: dataLessonDates }) => {
          let lessonGroups: InstructorGroupInfo[] = []
          const lessonDates = merge(this.state.lessonDates, dataLessonDates)
          if (!loading && !fetched && data && data.length) {
            lessonGroups = data
            lessonGroups.forEach(({ InstructorGroupInfo }) =>
              InstructorGroupInfo.GroupClassList.forEach((item) =>
                Object.assign(
                  item,
                  item?.VirtualClassRoomProvider?.toLowerCase() === VIRTUAL_CLASSROOM_PROVIDER.ZOOM
                    ? {
                        ZoomUsername: InstructorGroupInfo?.ZoomUsername,
                        VirtualClassRoomId: item?.VirtualClassRoomId,
                      }
                    : {}
                )
              )
            )
          }

          return (
            <EventAttendeeProvider
              StartDateTime={utcfyDate(
                selectedDate.clone().tz(profile?.Timezone).startOf('month').startOf('week').startOf('day')
              )}
              EndDateTime={utcfyDate(
                selectedDate.clone().tz(profile?.Timezone).endOf('month').endOf('week').endOf('day')
              )}
              RecordId={profile?.SFId}
              Type={profile?.Type}
            >
              <PageTitle title={'Instructor scheduling'} />
              <PageWithRightSidebarTpl
                body={
                  <SchedulingLayout activeTab={1} showClock>
                    <Layout
                      loading={loading}
                      fetchingLessonDates={fetchingLessonDates || loadingLessonDates}
                      instructorId={id}
                      currentDate={startOfDay.clone()}
                      setCurrentDate={(date: Date) => this.setCurrentDate(lessonGroups, date)}
                      onMonthChange={this.handleMonthChange}
                      lessonDates={lessonDates}
                      GroupClassList={this.getGroupClassList(lessonGroups, currentDate)}
                      profile={profile}
                      withPendingButtons={false}
                    />
                  </SchedulingLayout>
                }
                sidebar={
                  <Sidebar
                    currentDate={currentDate}
                    setCurrentDate={(date: Date) => this.setCurrentDate(lessonGroups, date)}
                    onMonthChange={this.handleMonthChange}
                    lessonDates={lessonDates}
                  />
                }
                toggleIcon={<Calendar size="md" />}
                showSidebar={false}
                showToggleOnMobile
              />
            </EventAttendeeProvider>
          )
        }}
      </QueryPageData>
    )
  }
}

const mapStateToProps = (state: { mySchedules: { date: string } }) => ({
  date: state.mySchedules.date,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setDate: bindActionCreators(setDate, dispatch),
})

const withConnect = connect(mapStateToProps, mapDispatchToProps)

export default WithPortalAuthorization(
  compose(withConnect, WithUserTimeHOC, WithLessonUtilsHOC, withApollo)(MySchedule),
  {
    id: 'page::instructors::scheduling',
  }
)
